// Sprint 6.7 — composer autocomplete state + data fetching.
//
// Owns:
//   • detection lifecycle (trigger char + query)
//   • debounced fetch against /api/workdir/index
//   • focused-item index for keyboard navigation
//   • accept handler that rewrites the textarea + fires onChange
//
// It does NOT own:
//   • textarea markup — that stays with the composer (ChatInput / ChatComposer)
//   • dropdown rendering — that's AutocompleteDropdown.tsx
//
// Kept deliberately small. Sprint consumers wire up three callbacks to their
// existing <textarea>: onChange → setValue, onKeyDown → hook.handleKeyDown,
// onSelect → hook.accept.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type RefObject,
  type SetStateAction,
} from 'react';
// `SetValue` deliberately accepts just a bare string — the hook never uses
// the updater-function form, and keeping it narrow lets zustand actions
// (e.g. `setComposerPrompt(prompt: string)`) be passed as-is without an
// extra useCallback wrapper in every consumer.
type SetValue = (value: string) => void;
import { fetchWorkdirIndex } from '../lib/api';
import {
  applyTrigger,
  detectTrigger,
  type DetectedTrigger,
} from '../lib/trigger-detection';
import type { WorkdirIndexEntry } from '../types';

export interface UseAutocompleteParams {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: SetValue;
  // Optional override for the fetcher (tests inject a mock).
  fetcher?: typeof fetchWorkdirIndex;
  // Debounce window before hitting the backend.
  debounceMs?: number;
  // When false (e.g. the composer is in a "send disabled" state), the hook
  // short-circuits all detection. Useful for read-only displays.
  disabled?: boolean;
}

export interface UseAutocompleteResult {
  trigger: DetectedTrigger | null;
  results: WorkdirIndexEntry[];
  loading: boolean;
  error: string | null;
  focusedIndex: number;
  setFocusedIndex: Dispatch<SetStateAction<number>>;
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean;
  accept: (entry: WorkdirIndexEntry) => void;
  close: () => void;
  // Unwrap the raw bare-char caret position (used in tests).
  caret: number;
}

const DEFAULT_DEBOUNCE_MS = 80;

export function useAutocomplete(
  params: UseAutocompleteParams,
): UseAutocompleteResult {
  const { textareaRef, value, setValue, disabled } = params;
  const fetcher = params.fetcher ?? fetchWorkdirIndex;
  const debounceMs = params.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const [caret, setCaret] = useState(0);
  const [trigger, setTriggerState] = useState<DetectedTrigger | null>(null);
  const [results, setResults] = useState<WorkdirIndexEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks which fetch is "current" — stale responses are dropped.
  const fetchSeqRef = useRef(0);

  // Close without touching the textarea content. Esc + click-outside route here.
  const close = useCallback(() => {
    setTriggerState(null);
    setResults([]);
    setError(null);
    setLoading(false);
    setFocusedIndex(0);
    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  // Listen for caret changes driven by focus / selection / keystrokes. The
  // textarea events fire before React commits, so we read inside the handler
  // attached below (via onKeyUp / onSelect hooked by the consumer is overkill;
  // instead we poll on value + document.activeElement). Simplest approach:
  // read caret on every `value` change.
  useEffect(() => {
    if (disabled) {
      close();
      return;
    }
    const el = textareaRef.current;
    if (!el) return;
    const nextCaret = el.selectionStart ?? 0;
    setCaret(nextCaret);
    const next = detectTrigger(value, nextCaret);
    // Only update when it actually changes — prevents render storms on
    // pure typing that doesn't affect the trigger.
    setTriggerState((prev) => {
      if (prev === null && next === null) return prev;
      if (prev && next && prev.char === next.char && prev.query === next.query && prev.start === next.start && prev.end === next.end) {
        return prev;
      }
      return next;
    });
  }, [value, textareaRef, disabled, close]);

  // Fetch results whenever the trigger changes. Debounced by `debounceMs`.
  useEffect(() => {
    if (trigger === null) {
      close();
      return;
    }

    if (debounceTimerRef.current !== null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Cancel any in-flight fetch before starting the new one.
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      fetcher({ trigger: trigger.char, q: trigger.query, signal: ctrl.signal })
        .then((res) => {
          if (seq !== fetchSeqRef.current) return; // stale
          setResults(res.results);
          setFocusedIndex(0);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (seq !== fetchSeqRef.current) return;
          if ((err as { name?: string }).name === 'AbortError') return;
          setError(
            err instanceof Error
              ? err.message
              : 'Could not load autocomplete',
          );
          setResults([]);
          setLoading(false);
        });
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [trigger, fetcher, debounceMs, close]);

  const accept = useCallback(
    (entry: WorkdirIndexEntry) => {
      if (trigger === null) return;
      const { text: newValue, caret: newCaret } = applyTrigger(
        value,
        trigger,
        entry.insertText,
      );
      setValue(newValue);
      // Restore focus + caret after React commits. Using rAF guarantees the
      // new value landed in the DOM before we call setSelectionRange.
      const el = textareaRef.current;
      if (el) {
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(newCaret, newCaret);
        });
      }
      close();
    },
    [trigger, value, setValue, textareaRef, close],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (trigger === null || results.length === 0) {
        // On Esc, even with no results, close so the `/` literal remains but
        // the empty dropdown disappears. Consumers still get the event.
        if (trigger !== null && e.key === 'Escape') {
          e.preventDefault();
          close();
          return true;
        }
        return false;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((i) => (i + 1) % results.length);
          return true;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((i) => (i - 1 + results.length) % results.length);
          return true;
        case 'Enter':
        case 'Tab': {
          e.preventDefault();
          const item = results[focusedIndex];
          if (item) accept(item);
          return true;
        }
        case 'Escape':
          e.preventDefault();
          close();
          return true;
        default:
          return false;
      }
    },
    [trigger, results, focusedIndex, accept, close],
  );

  // Clear state on unmount — belt-and-suspenders for the fetch abort.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return useMemo(
    () => ({
      trigger,
      results,
      loading,
      error,
      focusedIndex,
      setFocusedIndex,
      handleKeyDown,
      accept,
      close,
      caret,
    }),
    [trigger, results, loading, error, focusedIndex, handleKeyDown, accept, close, caret],
  );
}
